/** Manages player sprite animation state: hit flash, attack frames, walk/idle. */
import Phaser from 'phaser';
import { ConfigLoader } from '../systems/ConfigLoader';
import { COLS_PER_ROW, COL_IDLE, COL_ATTACK_WINDUP, COL_ATTACK_STRIKE, COL_HIT, COL_DEATH, facingToRow } from './SpriteLayout';

// How long the hit-reaction frame holds (ms). Shorter than enemy (200ms) — player recovers faster.
const HIT_FLASH_MS = 150;

export class PlayerAnimationController {
  private hitFlashTimer: number = 0;

  /** Trigger a hit flash on damage. */
  onDamage(): void {
    this.hitFlashTimer = HIT_FLASH_MS;
  }

  /** Clear hit flash state (e.g. on death or respawn). */
  reset(): void {
    this.hitFlashTimer = 0;
  }

  /** Update sprite frame based on current state: hit flash > attack > walk > idle. */
  update(
    sprite: Phaser.GameObjects.Sprite,
    moveX: number,
    moveY: number,
    delta: number,
    isAttacking: boolean,
    combatState: string,
    isRunning: boolean,
    facingX: number,
    facingY: number,
  ): void {
    const { row, flipX } = facingToRow(facingX, facingY);

    // Tick down hit flash
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= delta;
    }

    // Priority 1: Hit flash
    if (this.hitFlashTimer > 0) {
      sprite.anims.stop();
      sprite.setFrame(row * COLS_PER_ROW + COL_HIT);
      sprite.setFlipX(flipX);
      return;
    }

    // Priority 2: Attack animation
    if (isAttacking) {
      if (combatState === 'windup') {
        sprite.anims.stop();
        sprite.setFrame(row * COLS_PER_ROW + COL_ATTACK_WINDUP);
        sprite.setFlipX(flipX);
        return;
      }
      if (combatState === 'active') {
        sprite.anims.stop();
        sprite.setFrame(row * COLS_PER_ROW + COL_ATTACK_STRIKE);
        sprite.setFlipX(flipX);
        return;
      }
      // Recovery: fall through to idle/walk
    }

    // Priority 3: Walk or idle
    const animSpeed = isRunning ? ConfigLoader.movement.run.speedMultiplier : 1;
    if (moveX === 0 && moveY === 0) {
      sprite.anims.stop();
      sprite.setFrame(row * COLS_PER_ROW + COL_IDLE);
      sprite.setFlipX(flipX);
    } else if (Math.abs(moveX) > Math.abs(moveY)) {
      sprite.play('player-walk-east', true);
      sprite.setFlipX(moveX < 0);
    } else if (moveY > 0) {
      sprite.play('player-walk-south', true);
      sprite.setFlipX(false);
    } else {
      sprite.play('player-walk-north', true);
      sprite.setFlipX(false);
    }
    sprite.anims.timeScale = animSpeed;
  }

  /** Get the death frame for a given facing direction. */
  getDeathFrame(facingX: number, facingY: number): { frame: number; flipX: boolean } {
    const { row, flipX } = facingToRow(facingX, facingY);
    return { frame: row * COLS_PER_ROW + COL_DEATH, flipX };
  }

  /** Get the idle frame for a given facing direction. */
  getIdleFrame(facingX: number, facingY: number): { frame: number; flipX: boolean } {
    const { row, flipX } = facingToRow(facingX, facingY);
    return { frame: row * COLS_PER_ROW + COL_IDLE, flipX };
  }
}
